import React from 'react';
import { TRANSLATIONS } from '../../translations';

interface AuthScreenProps {
  showAuth: boolean;
  setShowAuth: (show: boolean) => void;
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  isSigningUp: boolean;
  setIsSigningUp: (isSigningUp: boolean) => void;
  handleSignUp: () => void;
  handleLogin: () => Promise<boolean>;
  theme: 'light' | 'dark';
  language: 'en' | 'pl';
}

export function AuthScreen({ showAuth, setShowAuth, email, setEmail, password, setPassword, isSigningUp, setIsSigningUp, handleSignUp, handleLogin, theme, language }: AuthScreenProps) {
  if (!showAuth) return null;
  const t = TRANSLATIONS[language];

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="bg-white dark:bg-slate-900 p-8 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-md relative">
        <button
          onClick={() => setShowAuth(false)}
          className="absolute top-4 right-4 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          X
        </button>
        <h2 className="font-heading text-3xl font-bold text-center text-orange-700 mb-6">{isSigningUp ? t.signUp : t.logIn}</h2>
        <input
          type="email"
          data-testid="auth-email-input"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full p-3 mb-4 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <input
          type="password"
          data-testid="auth-password-input"
          placeholder={isSigningUp ? t.newPasswordPlaceholder : t.passwordPlaceholder}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full p-3 mb-6 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        {isSigningUp && (
          <input
            type="password"
            placeholder={t.retypeNewPasswordPlaceholder}
            value={password} // Re-using password state for retypeNewPassword for simplicity
            onChange={(e) => setPassword(e.target.value)} // Re-using setPassword for setRetypeNewPassword
            className="w-full p-3 mb-6 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        )}
        <button
          type="button"
          data-testid="auth-submit-btn"
          onClick={async () => {
            if (isSigningUp) {
              handleSignUp();
              return;
            }
            const ok = await handleLogin();
            if (ok) setShowAuth(false);
          }}
          className="w-full bg-orange-600 text-white py-3 rounded-lg font-bold hover:bg-orange-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
        >
          {isSigningUp ? t.signUp : t.logIn}
        </button>
        <p className="text-center text-sm text-slate-500 mt-4">
          {isSigningUp ? t.haveAccount : t.noAccount}
          <button
            onClick={() => setIsSigningUp(!isSigningUp)}
            className="text-orange-700 underline underline-offset-2 ml-1"
          >
            {isSigningUp ? t.logIn : t.signUp}
          </button>
        </p>
      </div>
    </div>
  );
}
